class MediaCarouselComponent extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector('.media-carousel__track');
    this.items = this.querySelectorAll('.media-carousel__item');
    this.modal = this.querySelector('.media-carousel__modal');
    this.modalContent = this.querySelector('.media-carousel__modal-media');
    this.modalClose = this.querySelector('.media-carousel__modal-close');
    this.modalBackdrop = this.querySelector('.media-carousel__modal-backdrop');

    // 在移动端强制禁用自动滚动
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    this.isMobile = isMobile;
    this.isAutoplay = !isMobile && (this.track?.dataset.autoplay === 'true');
    this.speed = parseInt(this.track?.dataset.speed) || 4;
    this.isPaused = false;

    // 拖拽相关状态
    this.isDragging = false;
    this.justFinishedDragging = false;
    this.dragStartX = 0;
    this.dragStartTransform = 0;
    this.currentTransform = 0;
    this.animationStartTime = 0;
    this.animationStartTransform = 0;
    this.dragSetup = false;

    this.init();
  }

  init() {
    this.setupAutoplay();
    this.setupModalEvents();
    this.setupItemEvents();
    this.setupVideos();
    // 仅在PC端设置拖拽功能
    if (!this.isMobile) {
      this.setupDrag();
    }
  }

  setupAutoplay() {
    if (!this.isAutoplay || this.items.length === 0) return;

    const originalItems = Array.from(this.items);
    // Ensure content fills beyond container to avoid visible blank
    const groupWidth = this.ensureSeamlessContent(originalItems);

    // Provide scroll distance for CSS keyframes
    this.track.style.setProperty('--scroll-distance', `${Math.round(groupWidth)}px`);

    // Keep existing speed semantics: seconds per item
    const itemsCount = originalItems.length;
    const animationDuration = this.speed * itemsCount;
    this.normalDuration = animationDuration;
    this.track.style.animationDuration = `${animationDuration}s`;

    // Pause on hover, resume on leave (no restart, no misalignment)
    // 注意：拖拽时会禁用动画，所以这里只在非拖拽状态下暂停
    this.track.addEventListener('mouseenter', () => {
      if (!this.isDragging) {
        this.track.style.animationPlayState = 'paused';
      }
    });

    this.track.addEventListener('mouseleave', () => {
      if (!this.isPaused && !this.isDragging) {
        this.track.style.animationPlayState = 'running';
      }
    });
  }

  duplicateItems() {
    // Clone items to create seamless infinite scroll
    const itemsToClone = Math.min(this.items.length, 8); // Limit clones for performance

    for (let i = 0; i < itemsToClone; i++) {
      const clone = this.items[i].cloneNode(true);
      clone.classList.add('media-carousel__item--clone');
      this.track.appendChild(clone);
    }

    // Re-query items to include clones
    this.items = this.querySelectorAll('.media-carousel__item');
    this.setupItemEvents(); // Re-setup events for clones
  }

  setupItemEvents() {
    this.items.forEach(item => {
      // Remove existing event listeners to prevent duplicates
      const newItem = item.cloneNode(true);
      item.parentNode.replaceChild(newItem, item);
    });

    // Re-query after replacement
    this.items = this.querySelectorAll('.media-carousel__item');

    this.items.forEach(item => {
      item.addEventListener('click', (e) => {
        // 如果正在拖拽或刚刚完成拖拽，不触发点击事件
        if (this.isDragging || this.justFinishedDragging) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        e.preventDefault();
        this.openModal(item);
      });

      // Handle video hover effects
      const video = item.querySelector('.media-carousel__video');
      if (video) {
        item.addEventListener('mouseenter', () => {
          this.playVideo(video);
        });

        item.addEventListener('mouseleave', () => {
          this.pauseVideo(video);
        });
      }
    });
  }

  setupModalEvents() {
    if (!this.modal) return;

    this.modalClose?.addEventListener('click', () => {
      this.closeModal();
    });

    this.modalBackdrop?.addEventListener('click', () => {
      this.closeModal();
    });

    // Close modal on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('active')) {
        this.closeModal();
      }
    });
  }

  setupVideos() {
    const videos = this.querySelectorAll('.media-carousel__video');
    videos.forEach(video => {
      // Ensure videos are muted and ready for autoplay
      video.muted = true;
      video.load();
    });
  }

  playVideo(video) {
    if (video && video.paused) {
      // 保持当前位置播放，不强制重置到开头，避免点击暂停/播放导致进度回退
      const playPromise = video.play();

      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.log('Video autoplay failed:', error);
        });
      }
    }
  }

  pauseVideo(video) {
    if (video && !video.paused) {
      // 仅暂停，不重置 currentTime，避免用户暂停时进度回退
      video.pause();
    }
  }

  openModal(item) {
    if (!this.modal) return;

    // Pause carousel autoplay when modal is open
    this.isPaused = true;
    this.track.style.animationPlayState = 'paused';

    const isVideo = item.dataset.mediaType === 'video';
    const title = item.querySelector('.media-carousel__title')?.textContent || '';
    const description = item.querySelector('.media-carousel__description')?.textContent || '';

    // Clear modal content
    this.modalContent.innerHTML = '';

    if (isVideo) {
      const originalVideo = item.querySelector('.media-carousel__video');
      const videoSrc = originalVideo?.querySelector('source')?.src || originalVideo?.src;
      const posterSrc = item.querySelector('.media-carousel__video-poster')?.src;

      if (videoSrc) {
        const modalVideo = document.createElement('video');
        modalVideo.controls = true;
        modalVideo.autoplay = true;
        modalVideo.style.maxWidth = '100%';
        modalVideo.style.maxHeight = '90vh';

        if (posterSrc) {
          modalVideo.poster = posterSrc;
        }

        const source = document.createElement('source');
        source.src = videoSrc;
        source.type = videoSrc.includes('.webm') ? 'video/webm' : 'video/mp4';

        modalVideo.appendChild(source);
        this.modalContent.appendChild(modalVideo);
      }
    } else {
      const originalImage = item.querySelector('.media-carousel__image');
      if (originalImage) {
        const modalImage = document.createElement('img');
        modalImage.src = originalImage.src;
        modalImage.alt = originalImage.alt || title;
        modalImage.style.maxWidth = '100%';
        modalImage.style.maxHeight = '90vh';
        modalImage.style.objectFit = 'contain';

        this.modalContent.appendChild(modalImage);
      }
    }

    // Add title and description if available
    if (title || description) {
      const contentDiv = document.createElement('div');
      contentDiv.style.padding = '0rem';
      contentDiv.style.backgroundColor = 'rgb(var(--color-background))';

      if (title) {
        const titleEl = document.createElement('h3');
        titleEl.textContent = title;
        titleEl.style.margin = '0 0 0.5rem 0';
        contentDiv.appendChild(titleEl);
      }

      if (description) {
        const descEl = document.createElement('p');
        descEl.textContent = description;
        descEl.style.margin = '0';
        descEl.style.color = 'rgba(var(--color-foreground), 0.7)';
        contentDiv.appendChild(descEl);
      }

      this.modalContent.appendChild(contentDiv);
    }

    // Show modal
    this.modal.classList.add('active');
    document.body.style.overflow = 'hidden';
  }

  closeModal() {
    if (!this.modal) return;

    // Stop any playing videos in modal
    const modalVideo = this.modalContent.querySelector('video');
    if (modalVideo) {
      modalVideo.pause();
      modalVideo.currentTime = 0;
    }

    // Clear modal content
    this.modalContent.innerHTML = '';

    // Hide modal
    this.modal.classList.remove('active');
    document.body.style.overflow = '';

    // Resume carousel autoplay
    this.isPaused = false;
    if (this.isAutoplay) {
      this.track.style.animationPlayState = 'running';
    }
  }

  // Utilities for measuring and seamless content
  getItemsTotalWidth(items) {
    const gapPx = parseFloat(getComputedStyle(this.track).gap) || 0;
    let total = 0;
    items.forEach(el => {
      total += el.getBoundingClientRect().width;
    });
    total += gapPx * Math.max(items.length - 1, 0);
    return total;
  }

  ensureSeamlessContent(originalItems) {
    const container = this.querySelector('.media-carousel__container');
    const containerWidth = container?.clientWidth || this.clientWidth;

    let currentItems = Array.from(this.querySelectorAll('.media-carousel__item'));
    const groupWidth = this.getItemsTotalWidth(originalItems);

    while (this.getItemsTotalWidth(currentItems) < containerWidth + groupWidth) {
      originalItems.forEach(item => {
        const clone = item.cloneNode(true);
        clone.classList.add('media-carousel__item--clone');
        clone.setAttribute('aria-hidden', 'true');
        this.track.appendChild(clone);
      });
      currentItems = Array.from(this.querySelectorAll('.media-carousel__item'));
    }

    this.items = this.querySelectorAll('.media-carousel__item');
    this.setupItemEvents();

    return groupWidth;
  }

  setupDrag() {
    if (!this.track || this.isMobile) {
      return;
    }
    this.dragSetup = true;

    let dragThreshold = 5; // 拖拽阈值，小于此值视为点击
    let hasDragged = false;
    let clickedItem = null;

    // 获取当前transform值
    const updateCurrentTransform = () => {
      const style = window.getComputedStyle(this.track);
      const matrix = style.transform || style.webkitTransform;
      if (matrix && matrix !== 'none') {
        const values = matrix.match(/matrix.*\((.+)\)/);
        if (values) {
          this.currentTransform = parseFloat(values[1].split(',')[4]) || 0;
        }
      } else {
        this.currentTransform = 0;
      }
    };

    // 鼠标按下
    this.track.addEventListener('mousedown', (e) => {
      // 如果点击的是可交互元素（如按钮、链接），不触发拖拽
      if (e.target.closest('button, a, .media-carousel__play-button')) {
        return;
      }

      e.preventDefault();
      this.isDragging = true;
      hasDragged = false;
      this.dragStartX = e.clientX;
      clickedItem = e.target.closest('.media-carousel__item');
      
      // 暂停动画
      if (this.isAutoplay) {
        this.track.style.animationPlayState = 'paused';
        // 获取当前动画的transform值
        updateCurrentTransform();
        this.dragStartTransform = this.currentTransform;
        // 禁用动画，使用transform手动控制
        this.track.style.animation = 'none';
        this.track.style.transform = `translateX(${this.currentTransform}px)`;
      } else {
        updateCurrentTransform();
        this.dragStartTransform = this.currentTransform;
      }

      // 添加拖拽样式
      this.track.style.cursor = 'grabbing';
      this.track.style.userSelect = 'none';
      
      // 防止文本选择
      document.body.style.userSelect = 'none';
    });

    // 鼠标移动
    const handleMouseMove = (e) => {
      if (!this.isDragging) return;

      const deltaX = e.clientX - this.dragStartX;
      
      // 如果移动距离超过阈值，视为拖拽
      if (Math.abs(deltaX) > dragThreshold) {
        hasDragged = true;
      }

      const newTransform = this.dragStartTransform + deltaX;
      this.currentTransform = newTransform;
      this.track.style.transform = `translateX(${newTransform}px)`;
    };

    document.addEventListener('mousemove', handleMouseMove);

    // 鼠标释放
    const handleMouseUp = (e) => {
      if (!this.isDragging) return;

      const deltaX = e.clientX - this.dragStartX;
      const wasDragging = hasDragged || Math.abs(deltaX) > dragThreshold;

      this.isDragging = false;
      this.track.style.cursor = '';
      this.track.style.userSelect = '';
      document.body.style.userSelect = '';

      // 如果发生了拖拽，设置标志以防止误触发点击事件
      if (wasDragging) {
        this.justFinishedDragging = true;
        // 在150ms后清除标志，确保点击事件不会误触发
        setTimeout(() => {
          this.justFinishedDragging = false;
        }, 150);
      } else {
        // 如果没有拖拽（只是点击），触发item的点击事件
        if (clickedItem) {
          // 延迟触发，确保拖拽事件已完全结束
          setTimeout(() => {
            if (!this.justFinishedDragging) {
              const clickEvent = new MouseEvent('click', {
                bubbles: true,
                cancelable: true,
                view: window
              });
              clickedItem.dispatchEvent(clickEvent);
            }
          }, 10);
        }
      }

      clickedItem = null;

      // 如果启用自动播放，需要重新启动动画
      if (this.isAutoplay && !this.isPaused) {
        // 重新计算动画起始位置
        const originalItems = Array.from(this.querySelectorAll('.media-carousel__item:not(.media-carousel__item--clone)'));
        const groupWidth = this.getItemsTotalWidth(originalItems);
        
        // 计算当前应该在第几个循环
        const currentOffset = Math.abs(this.currentTransform);
        const cycles = Math.floor(currentOffset / groupWidth);
        const remainder = currentOffset % groupWidth;
        
        // 重置到最近的循环起点
        const animationStartOffset = -(cycles * groupWidth);
        this.track.style.transform = `translateX(${animationStartOffset}px)`;
        
        // 重新设置动画
        this.track.style.setProperty('--scroll-distance', `${Math.round(groupWidth)}px`);
        this.track.style.animation = 'scroll-horizontal';
        this.track.style.animationDuration = `${this.normalDuration}s`;
        this.track.style.animationDelay = '0s';
        
        // 使用animation-delay来同步到正确的位置
        if (remainder > 0) {
          const progress = remainder / groupWidth;
          const delay = -(progress * this.normalDuration);
          this.track.style.animationDelay = `${delay}s`;
        }
        
        // 重新启动动画
        this.track.style.animationPlayState = 'running';
      }
    };

    document.addEventListener('mouseup', handleMouseUp);

    // 鼠标离开窗口时也释放
    document.addEventListener('mouseleave', handleMouseUp);
  }

  // Method to handle dynamic content changes
  refresh() {
    this.items = this.querySelectorAll('.media-carousel__item:not(.media-carousel__item--clone)');
    this.setupItemEvents();
    this.setupVideos();

    if (this.isAutoplay) {
      this.setupAutoplay();
    }

    // 重新设置拖拽功能（如果之前已设置）
    if (!this.isMobile && !this.dragSetup) {
      this.setupDrag();
      this.dragSetup = true;
    }
  }
}

// Register the custom element
customElements.define('media-carousel-component', MediaCarouselComponent);

// Handle theme editor events for Shopify admin
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const mediaCarousel = event.target.querySelector('media-carousel-component');
    if (mediaCarousel) {
      mediaCarousel.refresh();
    }
  });

  document.addEventListener('shopify:section:reorder', (event) => {
    const mediaCarousel = event.target.querySelector('media-carousel-component');
    if (mediaCarousel) {
      mediaCarousel.refresh();
    }
  });

  document.addEventListener('shopify:block:select', (event) => {
    const mediaCarousel = event.target.closest('media-carousel-component');
    if (mediaCarousel) {
      mediaCarousel.isPaused = true;
      mediaCarousel.track.style.animationPlayState = 'paused';
    }
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const mediaCarousel = event.target.closest('media-carousel-component');
    if (mediaCarousel && mediaCarousel.isAutoplay) {
      mediaCarousel.isPaused = false;
      mediaCarousel.track.style.animationPlayState = 'running';
    }
  });
}